import { useEffect, useMemo, useState } from 'react';

import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { YearbookParams } from '../..';
import { GradientCircle } from '../../components/GradientCircle';
import { ShareButton } from '../../components/ShareButton';
import { colors, sectionTitleGradient } from '../../constants';
import { MarketVideo } from '../../data/videos';
import { getVideoEmbedUrl, getVideoThumbnailUrl } from '../../utils/videos';

interface VideosSectionProps {
  id: string;
  videos: MarketVideo[];
}

const title = 'Vídeos';

export function VideosSection({ id, videos }: VideosSectionProps) {
  const { market } = useParams<YearbookParams>();
  const [selectedVideoId, setSelectedVideoId] = useState(
    videos[0]?.videoId ?? '',
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  useEffect(() => {
    if (!videos.length) {
      setSelectedVideoId('');
      return;
    }

    const selectedExists = videos.some(
      (video) => video.videoId === selectedVideoId,
    );

    if (!selectedExists) {
      setSelectedVideoId(videos[0].videoId);
    }
  }, [selectedVideoId, videos]);

  const selectedVideo = useMemo(() => {
    return (
      videos.find((video) => video.videoId === selectedVideoId) ??
      videos[0] ??
      null
    );
  }, [selectedVideoId, videos]);

  const videoEmbedUrl = selectedVideo
    ? getVideoEmbedUrl(selectedVideo.videoId)
    : '';

  const { data: thumbnails } = useQuery({
    queryKey: [
      'yearbook-2026-video-thumbnails',
      videos.map((video) => video.videoId).join(','),
    ],
    queryFn: async () => {
      const thumbnailUrls = await Promise.all(
        videos.map((video) => getVideoThumbnailUrl(video.videoId)),
      );

      return videos.reduce(
        (acc, video, index) => {
          acc[video.videoId] = thumbnailUrls[index];
          return acc;
        },
        {} as Record<string, string>,
      );
    },
    enabled: videos.length > 0,
  });

  if (!videos.length || !selectedVideo) {
    return null;
  }

  return (
    <Box
      component="section"
      id={id}
      display="flex"
      flexDirection="column"
      gap={4}
      mt={10}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center',
        }}>
        <Typography
          component="h2"
          fontSize={{ xs: 28, md: 40 }}
          fontWeight={700}
          lineHeight={1.2}
          sx={{
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundImage: sectionTitleGradient,
          }}>
          {title}
        </Typography>
        <ShareButton market={market} section={kebabcase(title)} />
        <GradientCircle right="0" size={isMobile ? 300 : 500} />
      </Box>

      <Box
        sx={{
          bgcolor: colors.bgSection,
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography
            color="white"
            fontWeight={700}
            fontSize={{ xs: '1rem', md: '1.2rem' }}>
            {selectedVideo.title}
          </Typography>
          <Typography
            color={colors.text}
            fontSize="0.85rem"
            textTransform="uppercase"
            letterSpacing={1}>
            {selectedVideo.author}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.08)',
            paddingTop: '56.25%',
            background:
              'linear-gradient(135deg, rgba(255,130,17,0.18), rgba(0,0,0,0.35))',
          }}>
          <Box
            component="iframe"
            src={videoEmbedUrl}
            title={selectedVideo.title}
            allow="autoplay; clipboard-write; encrypted-media; web-share"
            allowFullScreen
            width="100%"
            height="100%"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              border: 'none',
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}>
          {videos.map((video) => {
            const isActive = selectedVideo.videoId === video.videoId;
            const thumbnail = thumbnails?.[video.videoId];

            return (
              <Box
                key={video.videoId}
                onClick={() => setSelectedVideoId(video.videoId)}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isActive
                    ? 'rgba(255,130,17,0.9)'
                    : 'rgba(255,255,255,0.08)',
                  backgroundColor: isActive
                    ? 'rgba(255,130,17,0.08)'
                    : 'rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(255,130,17,0.9)',
                    backgroundColor: 'rgba(255,130,17,0.08)',
                    transform: 'translateY(-2px)',
                  },
                }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%',
                    background:
                      'linear-gradient(145deg, rgba(255,130,17,0.28), rgba(33,33,33,0.55))',
                  }}>
                  {thumbnail && (
                    <Box
                      component="img"
                      src={thumbnail}
                      alt={video.title}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.08) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        border: '1px solid rgba(255,255,255,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <PlayArrowRoundedIcon sx={{ color: 'white' }} />
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}>
                  <Typography
                    color="white"
                    fontSize="0.8rem"
                    fontWeight={700}
                    lineHeight={1.35}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                    {video.title}
                  </Typography>
                  <Typography
                    color={colors.textDim}
                    fontSize="0.7rem"
                    letterSpacing={0.6}>
                    {video.author}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
